import { Component, OnInit } from '@angular/core';
import { KPIService } from 'app/modules/shared/services/kpi.service';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import {
    ClientKPIConfigWebModel,
    ClientKPIBandDataModel,
    BandInfo,
    ClientKPIWebModelComplete,
} from 'app/modules/shared/interfaces/kpi-data';
import { forkJoin } from 'rxjs';
import { NegativeToParensPipe } from 'app/modules/shared/pipes/negative-to-parens.pipe';
import { CurrencyPipe } from '@angular/common';
import { ReportingService } from 'app/modules/reports/services/reporting.service';
import { saveAs } from 'file-saver';
import {
    ITableColumnProperties,
    ITableDataProperties,
} from '../../../shared/components/common-kendo-table/common-kendo-table.component';
import {
    NoDataActionType,
    NoDataSourceType,
    NoDataTypes,
} from '../../../shared/interfaces/no-data';
import {
    IQuickReportExport,
    IRow,
    ITableColumns,
} from '../../../reports/interfaces/reporting-data';
import { IScorecardKPIInfo } from '../../../settings/components/kpi-scorecard/kpi-scorecard-models';
import { CommonKendoTableService } from '../../../shared/services/common-kendo-table.service';

@Component({
    selector: 'app-performance-kpi',
    templateUrl: './performance-kpi.component.html',
    styleUrls: ['./performance-kpi.component.scss'],
})
export class PerformanceKpiComponent implements OnInit {
    sortAscending: boolean = true;
    config: ClientKPIConfigWebModel;
    kpiMasterList: IScorecardKPIInfo[];
    kpiConfigList: IScorecardKPIInfo[];
    kpiTableColumnProperties: ITableColumnProperties;
    kpiTableDataProperties: ITableDataProperties;
    searchTerm: string;
    action: NoDataActionType;
    noDataType: NoDataTypes;

    constructor(
        private kpiService: KPIService,
        private negativeToParensPipe: NegativeToParensPipe,
        private reusableFunctions: ReusableFunctions,
        private currencyPipe: CurrencyPipe,
        private reportingService: ReportingService,
        private commKendoTbleService: CommonKendoTableService
    ) {}

    ngOnInit() {
        // TODO: clean up the formatting and Logic
        this.searchTerm = '';
        this.action = NoDataActionType.pageStart;
        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: this.searchTerm,
            componentTitle: 'KPIs',
            inputSource: NoDataSourceType.table,
        };

        this.initiateTable();

        forkJoin(
            this.kpiService.getCurrentKPIConfiguration(),
            this.kpiService.getKPIMasterList()
        ).subscribe(([config, list]) => {
            this.config = config;
            this.kpiMasterList = list;
            this.kpiConfigList = this.kpiMasterList.filter((kpi) => {
                return this.doesKPIMasterKeyExistInConfig(kpi);
            });

            this.sortKPIs();
            this.populatePerformanceBands();
            this.updateKPIGrid();
        });
    }

    updateKPIGrid() {
        this.kpiTableDataProperties.isDataLoaded = false;
        this.kpiTableDataProperties.hasData = false;
        let data: IScorecardKPIInfo[] = this.kpiConfigList;

        data.forEach((kpi, index) => {
            kpi.targetFormatted = this.getTarget(index);
            kpi.poor = this.getUnacceptableRange(index);
            kpi.fair = this.getBelowAverageRange(index);
            kpi.good = this.getAcceptableRange(index);
            kpi.veryGood = this.getAboveAverageRange(index);
            kpi.excellent = this.getExceptionalRange(index);
            kpi.weightedByLA = kpi.isWeightedByLA ? 'Yes' : 'No';
        });

        if (this.searchTerm && this.searchTerm.length > 0) {
            data = this.commKendoTbleService.searchHandler(
                data,
                this.kpiTableColumnProperties.columnProperties,
                this.searchTerm
            );
        }

        this.kpiTableDataProperties.gridData = data;
        this.kpiTableDataProperties.hasData = data.length > 0;
        this.kpiTableDataProperties.isDataLoaded = true;
        this.kpiTableDataProperties = Object.assign({}, this.kpiTableDataProperties);
    }

    initiateTable() {
        this.kpiTableDataProperties = {
            action: this.action,
            gridData: [],
            searchTerm: this.searchTerm,
            componentTitle: '',
            initialPageSize: 500,
            initialSkip: 0,
            maxHeight: 700,
            recordSelection: [],
            kendoGridSelectById: '',
            hasData: false,
            isDataLoaded: false,
            class: 'common-kendo-table-no-box-shadow',
            pageable: false,
            useLGScroll: false,
        };

        let kpiTableColumnProperties: ITableColumns[] = [
            {
                fieldId: 'kpiDescription',
                fieldName: 'KEY PERFORMANCE INDICATOR',
                isHyperlink: false,
                fieldOrder: 0,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'string',
                width: 250,
                filterable: false,
                filter: 'text',
                allowSearch: true,
            },
            {
                fieldId: 'kpiCategory',
                fieldName: 'Category',
                isHyperlink: false,
                fieldOrder: 1,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'string',
                width: 110,
                filterable: false,
                filter: 'text',
                allowSearch: true,
            },
            {
                fieldId: 'weightedByLA',
                fieldName: 'Weighted by Loan Amount',
                isHyperlink: false,
                fieldOrder: 2,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'string',
                width: 160,
                filterable: false,
                filter: 'text',
                allowSearch: true,
            },
            {
                fieldId: 'targetFormatted',
                fieldName: 'TARGET',
                isHyperlink: false,
                fieldOrder: 3,
                isSortable: false,
                headerAlignment: 'right',
                dataAlignment: 'right',
                dataType: 'string',
                decimalPlaces: 1,
                width: 120,
                filterable: false,
                filter: 'text',
                allowSearch: true,
            },
            {
                fieldId: 'poor',
                fieldName: 'POOR',
                fieldOrder: 4,
                isSortable: false,
                headerAlignment: 'right',
                dataAlignment: 'right',
                dataType: 'string',
                decimalPlaces: 0,
                width: 120,
                filterable: false,
                hideIf: 'excel',
                columnBackgroundColor: '#fddbdb',
                headerBackgroundColor: '#fddbdb',
                allowSearch: true,
            },
            {
                fieldId: 'fair',
                fieldName: 'FAIR',
                fieldOrder: 5,
                isSortable: false,
                headerAlignment: 'right',
                dataAlignment: 'right',
                dataType: 'string',
                decimalPlaces: 0,
                width: 120,
                filterable: false,
                columnBackgroundColor: '#fff9e7',
                headerBackgroundColor: '#fff9e7',
                allowSearch: true,
            },
            {
                fieldId: 'good',
                fieldName: 'GOOD',
                isHyperlink: false,
                fieldOrder: 6,
                isSortable: false,
                headerAlignment: 'right',
                dataAlignment: 'right',
                dataType: 'string',
                decimalPlaces: 0,
                width: 120,
                filterable: false,
                hideIf: 'excel',
                columnBackgroundColor: '#e0f3f3',
                headerBackgroundColor: '#e0f3f3',
                allowSearch: true,
            },
            {
                fieldId: 'veryGood',
                fieldName: 'VERY GOOD',
                isHyperlink: false,
                isHierarchyEntity: false,
                fieldOrder: 7,
                isSortable: false,
                headerAlignment: 'right',
                dataAlignment: 'right',
                dataType: 'string',
                decimalPlaces: 0,
                width: 120,
                filterable: false,
                columnBackgroundColor: '#f7efff',
                headerBackgroundColor: '#f7efff',
                allowSearch: true,
            },
            {
                fieldId: 'excellent',
                fieldName: 'EXCELLENT',
                isHyperlink: false,
                fieldOrder: 8,
                isSortable: false,
                headerAlignment: 'right',
                dataAlignment: 'right',
                dataType: 'string',
                decimalPlaces: 0,
                width: 120,
                filterable: false,
                hideIf: 'excel',
                columnBackgroundColor: '#f3f9ed',
                headerBackgroundColor: '#f3f9ed',
                allowSearch: true,
            },
        ];

        this.kpiTableColumnProperties = {
            columnProperties: kpiTableColumnProperties,
        };
    }

    populatePerformanceBands(): any {
        this.kpiConfigList.forEach((clientKPI, i) => {
            this.config.clientKPIData.find((kpi) => {
                if (this.KPIsAreEqual(kpi, clientKPI)) {
                    clientKPI.isWeightedByLA = kpi.isWeightedByLA;

                    clientKPI.bandInfo = this.getBandInfoFromConfigData(kpi.performanceBands);
                    clientKPI.bandInfo.target = kpi.target;
                    return true;
                }
            });
        });
    }

    getBandInfoFromConfigData(performanceBands: ClientKPIBandDataModel[]): BandInfo {
        return {
            unacceptableStart: performanceBands[0].rangeEnd,
            belowAverageStart: performanceBands[1].rangeStart,
            belowAverageEnd: performanceBands[1].rangeEnd,
            acceptableStart: performanceBands[2].rangeStart,
            acceptableEnd: performanceBands[2].rangeEnd,
            aboveAverageStart: performanceBands[3].rangeStart,
            aboveAverageEnd: performanceBands[3].rangeEnd,
            exceptionalStart: performanceBands[4].rangeStart,
        };
    }

    doesKPIMasterKeyExistInConfig(kpi: IScorecardKPIInfo): any {
        let exists = false;
        this.config.clientKPIData.find((clientKPI) => {
            if (this.KPIsAreEqual(clientKPI, kpi)) {
                exists = true;
                return true;
            }
        });
        return exists;
    }

    KPIsAreEqual(clientKPI: ClientKPIWebModelComplete, kpi: IScorecardKPIInfo): any {
        return (
            clientKPI.kpiMasterKey === kpi.kpiMasterKey &&
            (clientKPI.kpiMilestoneKey === null
                ? true
                : clientKPI.kpiMilestoneKey === kpi.kpiMilestoneKey) &&
            (clientKPI.kpiMilestoneStaffingKey == null
                ? true
                : clientKPI.kpiMilestoneStaffingKey === kpi.kpiMilestoneStaffingKey)
        );
    }

    getTarget(index: number): string {
        if (!this.kpiConfigList[index].bandInfo) {
            return '';
        }
        return this.formatKPI(
            this.reusableFunctions.padToMaxPrecision(
                this.kpiConfigList[index].bandInfo.target,
                this.kpiConfigList[index].precision
            ),
            index
        );
    }

    getBelowAverageRange(index: number): string {
        if (!this.kpiConfigList[index].bandInfo) {
            return '';
        }
        return (
            this.formatKPI(
                this.reusableFunctions.padToMaxPrecision(
                    this.kpiConfigList[index].bandInfo.belowAverageStart,
                    this.kpiConfigList[index].precision
                ),
                index
            ) +
            ' - ' +
            this.formatKPI(
                this.reusableFunctions.padToMaxPrecision(
                    this.kpiConfigList[index].bandInfo.belowAverageEnd,
                    this.kpiConfigList[index].precision
                ),
                index
            )
        );
    }

    getAboveAverageRange(index: number): string {
        if (!this.kpiConfigList[index].bandInfo) {
            return '';
        }
        return (
            this.formatKPI(
                this.reusableFunctions.padToMaxPrecision(
                    this.kpiConfigList[index].bandInfo.aboveAverageStart,
                    this.kpiConfigList[index].precision
                ),
                index
            ) +
            ' - ' +
            this.formatKPI(
                this.reusableFunctions.padToMaxPrecision(
                    this.kpiConfigList[index].bandInfo.aboveAverageEnd,
                    this.kpiConfigList[index].precision
                ),
                index
            )
        );
    }

    getAcceptableRange(index: number): string {
        if (!this.kpiConfigList[index].bandInfo) {
            return '';
        }
        return (
            this.formatKPI(
                this.reusableFunctions.padToMaxPrecision(
                    this.kpiConfigList[index].bandInfo.acceptableStart,
                    this.kpiConfigList[index].precision
                ),
                index
            ) +
            ' - ' +
            this.formatKPI(
                this.reusableFunctions.padToMaxPrecision(
                    this.kpiConfigList[index].bandInfo.acceptableEnd,
                    this.kpiConfigList[index].precision
                ),
                index
            )
        );
    }

    getUnacceptableRange(index: number): string {
        if (!this.kpiConfigList[index].bandInfo) {
            return '';
        }
        let bestScoreHighest =
            this.kpiConfigList[index].bestScore.toLocaleLowerCase() === 'highest';
        return bestScoreHighest
            ? '<= ' +
                  this.formatKPI(
                      this.reusableFunctions.padToMaxPrecision(
                          this.kpiConfigList[index].bandInfo.unacceptableStart,
                          this.kpiConfigList[index].precision
                      ),
                      index
                  )
            : '>= ' +
                  this.formatKPI(
                      this.reusableFunctions.padToMaxPrecision(
                          this.kpiConfigList[index].bandInfo.unacceptableStart,
                          this.kpiConfigList[index].precision
                      ),
                      index
                  );
    }

    getExceptionalRange(index: number): string {
        if (!this.kpiConfigList[index].bandInfo) {
            return '';
        }
        let bestScoreHighest =
            this.kpiConfigList[index].bestScore.toLocaleLowerCase() === 'highest';
        return bestScoreHighest
            ? '>= ' +
                  this.formatKPI(
                      this.reusableFunctions.padToMaxPrecision(
                          this.kpiConfigList[index].bandInfo.exceptionalStart,
                          this.kpiConfigList[index].precision
                      ),
                      index
                  )
            : '<= ' +
                  this.formatKPI(
                      this.reusableFunctions.padToMaxPrecision(
                          this.kpiConfigList[index].bandInfo.exceptionalStart,
                          this.kpiConfigList[index].precision
                      ),
                      index
                  );
    }

    getBelowAverageEnd(index: number): string {
        if (!this.kpiConfigList[index].bandInfo) {
            return '';
        }
        return this.formatKPI(
            this.reusableFunctions.padToMaxPrecision(
                this.kpiConfigList[index].bandInfo.belowAverageEnd,
                this.kpiConfigList[index].precision
            ),
            index
        );
    }

    getAboveAverageStart(index: number): string {
        if (!this.kpiConfigList[index].bandInfo) {
            return '';
        }
        return this.formatKPI(
            this.reusableFunctions.padToMaxPrecision(
                this.kpiConfigList[index].bandInfo.aboveAverageStart,
                this.kpiConfigList[index].precision
            ),
            index
        );
    }

    exportData() {
        const exportRequest: IQuickReportExport = {
            reportDataSource: 'kpi-targets-ranges',
            columns: [
                {
                    columnId: 1,
                    columnName: 'KEY PERFORMANCE INDICATOR',
                },
                {
                    columnId: 2,
                    columnName: 'CATEGORY',
                },
                {
                    columnId: 10,
                    columnName: 'WEIGHTED BY LOAN AMOUNT',
                },
                {
                    columnId: 3,
                    columnName: 'TARGET',
                },
                {
                    columnId: 4,
                    columnName: 'POOR',
                },
                {
                    columnId: 5,
                    columnName: 'FAIR',
                },

                {
                    columnId: 6,
                    columnName: 'GOOD',
                },

                {
                    columnId: 7,
                    columnName: 'VERY GOOD',
                },

                {
                    columnId: 8,
                    columnName: 'EXCELLENT',
                },
            ],
            rows: [],
        };
        let kpiIndex = 0;
        for (const kpi of this.kpiConfigList) {
            const row: IRow = {
                rowId: kpiIndex + 1,
                rowData: [
                    { columnId: 1, dataType: 'String', value: kpi.kpiDescription },
                    { columnId: 2, dataType: 'String', value: kpi.kpiCategory },
                    { columnId: 10, dataType: 'String', value: kpi.isWeightedByLA ? 'Yes' : 'No' },
                    { columnId: 3, dataType: 'String', value: this.getTarget(kpiIndex) },
                    {
                        columnId: 4,
                        dataType: 'String',
                        value: this.getUnacceptableRange(kpiIndex)
                            .replace('<strong>&ge;</strong>', '>=')
                            .replace('<strong>&le;</strong>', '<='),
                    },
                    { columnId: 5, dataType: 'String', value: this.getBelowAverageRange(kpiIndex) },
                    { columnId: 6, dataType: 'String', value: this.getAcceptableRange(kpiIndex) },
                    { columnId: 7, dataType: 'String', value: this.getAboveAverageRange(kpiIndex) },
                    {
                        columnId: 8,
                        dataType: 'String',
                        value: this.getExceptionalRange(kpiIndex)
                            .replace('<strong>&ge;</strong>', '>=')
                            .replace('<strong>&le;</strong>', '<='),
                    },
                ],
            };
            exportRequest.rows.push(row);
            kpiIndex++;
        }
        this.reportingService.quickReportExportTo(exportRequest, 'excel').subscribe((res) => {
            if (res) {
                let dateTime = this.reusableFunctions.getCurrentDateTimeStamp();
                let reportName = exportRequest.reportDataSource + '_' + dateTime + '_.xlsx';
                saveAs(res, reportName);
            }
        });
    }

    formatKPI(value: string, index: number): string {
        if (this.kpiConfigList[index].isPercentage) {
            return this.negativeToParensPipe.transform(value + '%');
        } else if (this.kpiConfigList[index].kpiFormat === 'currency') {
            return this.currencyPipe.transform(value, '$', 'symbol-narrow', '1.0-0');
        } else {
            return this.negativeToParensPipe.transform(value);
        }
    }

    private sortKPIs() {
        this.kpiConfigList.sort((a, b) => {
            let aCat = a.kpiCategory.toLowerCase();
            let bCat = b.kpiCategory.toLowerCase();
            if (aCat < bCat) {
                if (this.sortAscending) {
                    return -1;
                } else {
                    return 1;
                }
            }
            if (aCat > bCat) {
                if (this.sortAscending) {
                    return 1;
                } else {
                    return -1;
                }
            } else {
                let aDesc = a.kpiDescription.toLowerCase();
                let bDesc = b.kpiDescription.toLowerCase();
                if (aDesc < bDesc) {
                    if (this.sortAscending) {
                        return -1;
                    } else {
                        return 1;
                    }
                }
                if (aDesc > bDesc) {
                    if (this.sortAscending) {
                        return 1;
                    } else {
                        return -1;
                    }
                } else {
                    return 0;
                }
            }
        });
    }

    searchRows(searchTerm) {
        this.searchTerm = searchTerm;
        let timeout = null;
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            this.noDataType.action = NoDataActionType.search;
            this.noDataType.searchTerm = this.searchTerm;
            this.updateKPIGrid();
        }, 1000);
    }
}
